import type { IPolicy } from '@adornis/abac/core/types.js';
import { isNullish } from '@adornis/assert/isNullish.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import {
  ensureLoggedInPayload,
  type LoggedInEvaluationFunction,
} from '../../permissions/abac-payloads/checkLoggedInPayload.js';
import { MARTINUS_ABAC_PAYLOAD } from '../../permissions/abac-payloads/MartinusAbacPayload.js';
import { getSchoolIDsForRoles, usersHaveSameSchoolOnRoles } from '../../permissions/api/getSchoolIDsForRoles.js';
import { hasRoles } from '../../permissions/api/hasRoles.js';
import { isParentOf } from '../family/isParentOf.js';
import { isSelf } from '../../permissions/api/isSelf.js';
import {
  DYNAMIC_ROLES,
  EMPLOYEE_ROLES,
  Role,
  SBS_ROLES,
  SPENDER_ROLES,
  STUDENT_ROLES,
} from '../../permissions/roles.js';
import type { UserOnUserPayload } from '../UserPolicy.js';

/**
 * This policies holds everything which is not in subcomponent. With the time this should probably clear up as sub component define thjeir own policies
 */

export enum UserDetailsActions {
  /**
   * Can see sorgebrechtigten Tab
   * TODO maybe move this to own policy for children at some point
   */
  VIEW_SORGBERECHTIGTEN_TAB = 'VIEW_SORGBERECHTIGTEN_TAB',

  /**
   *
   */
  VIEW_STUDENT_TAB = 'VIEW_STUDENT_TAB',
  VIEW_PERMISSION_MATRIX = 'VIEW_PERMISSION_MATRIX',
  EDIT_PERMISSION_MATRIX = 'EDIT_PERMISSION_MATRIX',
  VIEW_TIMELINE = 'VIEW_TIMELINE',
  VIEW_BUCHALTUNG = 'VIEW_BUCHALTUNG',
  VIEW_MICROSOFT = 'VIEW_MICROSOFT',

  VIEW_SEPA_MANDATES = 'VIEW_SEPA_MANDATES',
  REMOVE_CHILDREN_FROM_SBS_TEMP = 'REMOVE_CHILDREN_FROM_SBS_TEMP',
}

const UserDetailsPolicyFunctions: Record<UserDetailsActions, LoggedInEvaluationFunction<UserOnUserPayload>> = {
  /**
   * A user can see himself and all other users which he has permission on for the school.
   * Dynamic roles are used to determine in which context a user lives
   */
  [UserDetailsActions.VIEW_SORGBERECHTIGTEN_TAB]: async request => {
    /**
     * Check if it is the user himself
     */
    if (isSelf(request.payload.currentUser, request.payload.viewedUser)) {
      return { allowed: true, reason: 'Nutzer kann sich selbst sehen.' };
    }

    /**
     * Check if the currentUser is a parent of the viewed user
     */
    if (isParentOf(request.payload.currentUser, request.payload.viewedUser, ['sorgeberechtigt'])) {
      return { allowed: true, reason: 'Nutzer kann die Daten seiner Kinder sehen.' };
    }

    /**
     * Check if the currentUser can see the student or sbs
     */
    const allowedRolesOnStudent = [
      Role.SPENDEN_ADMIN,
      Role.SCHULLEITER,
      Role.BUCHHALTUNG,
      Role.SEKRETAER,
      Role.SUS_BEWERBER_BEARBEITUNG,
    ];

    const hasCommonContextsStudents = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnStudent,
      rolesViewedUser: [...STUDENT_ROLES, ...SBS_ROLES, ...SPENDER_ROLES],
    });
    if (hasCommonContextsStudents) {
      return {
        allowed: true,
        reason: 'Nutzer darf den Schüler sehen. Da er eine sehende Rolle auf der gleichen Schule hat.',
      };
    }

    /**
     * Check if the currentUser can see the employee
     */
    const allowedRolesOnEmployee = [Role.SPENDEN_ADMIN, Role.SCHULLEITER, Role.BUCHHALTUNG, Role.PSB, Role.MAV];

    const hasCommonContextsEmployee = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnEmployee,
      rolesViewedUser: EMPLOYEE_ROLES,
    });
    if (hasCommonContextsEmployee) {
      return {
        allowed: true,
        reason: 'Nutzer darf den Mitarbeiter sehen. Da er eine sehende Rolle auf der gleichen Schule hat.',
      };
    }

    return {
      allowed: false,
      reason: 'Nutzer ist nicht an der gleichen Schule.',
    };
  },
  /**
   * Only global psb can see the permission matrix
   */
  [UserDetailsActions.VIEW_PERMISSION_MATRIX]: async request => {
    if (hasRoles({ user: request.payload.currentUser, roles: [Role.PSB], contexts: [GLOBAL_CONTEXT] })) {
      return {
        allowed: true,
        reason: 'Nutzer ist globaler PSB und darf die Rechteverwaltung sehen.',
      };
    }

    const hasCommonContextsForUser = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: [Role.IT],
      rolesViewedUser: DYNAMIC_ROLES,
    });

    if (hasCommonContextsForUser) {
      return {
        allowed: true,
        reason: 'Nutzer hat die IT Rolle in der Schule der Person.',
      };
    }

    return {
      allowed: false,
      reason: 'Nutzer darf die Berechtigungsübersicht sehen.',
    };
  },

  [UserDetailsActions.EDIT_PERMISSION_MATRIX]: async () => {
    return {
      allowed: false,
      reason: 'Kein Nutzer außer Admin darf Rollen ändern.',
    };
  },

  /**
   * Global psb sees every timeline, Buchhaltung those of the persons of its schools - the timeline shows bank data,
   * which Buchhaltung sees anyway.
   */
  [UserDetailsActions.VIEW_TIMELINE]: async request => {
    const { currentUser, viewedUser } = request.payload;
    if (hasRoles({ user: currentUser, roles: [Role.PSB, Role.BUCHHALTUNG], contexts: [GLOBAL_CONTEXT] }))
      return { allowed: true, reason: 'Nutzer darf alle Timelines sehen.' };

    /**
     * The version operations take document IDs, not a person, so they are checked without a viewed user. The
     * person's tab and its document list are checked per person. Not `usersHaveSameSchoolOnRoles`: it lets any
     * global role through, Buchhaltung or not.
     */
    if (hasRoles({ user: currentUser, roles: [Role.BUCHHALTUNG] })) {
      const schoolIDs = await getSchoolIDsForRoles(currentUser, [Role.BUCHHALTUNG]);
      const viewedSchoolIDs = await getSchoolIDsForRoles(viewedUser, [
        ...SBS_ROLES,
        ...STUDENT_ROLES,
        ...SPENDER_ROLES,
        ...EMPLOYEE_ROLES,
      ]);
      if (!viewedUser || schoolIDs.some(schoolID => viewedSchoolIDs.includes(schoolID)))
        return { allowed: true, reason: 'Buchhaltung darf die Timeline der Personen ihrer Schule sehen.' };
    }
    return { allowed: false, reason: 'Nutzer darf die Timeline nicht sehen.' };
  },

  /**
   * Only persons with the buchhaltungs roles on the same school of the user can see this
   */
  [UserDetailsActions.VIEW_BUCHALTUNG]: async request => {
    const allowedRolesOnUser = [Role.BUCHHALTUNG];

    const hasCommonContexts = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnUser,
      rolesViewedUser: [...SBS_ROLES, ...STUDENT_ROLES, ...SPENDER_ROLES, ...EMPLOYEE_ROLES],
    });
    if (hasCommonContexts) {
      return {
        allowed: true,
        reason: 'Nutzer darf die Buchhaltung sehen.',
      };
    }
    return {
      allowed: false,
      reason: 'Nutzer darf die Buchhaltung sehen.',
    };
  },

  /**
   * Only global psb can see the user microsoft data
   */
  [UserDetailsActions.VIEW_MICROSOFT]: async request => {
    const allowedRolesOnUser = [Role.PSB, Role.SCHULLEITER, Role.IT];

    const hasCommonContexts = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnUser,
      rolesViewedUser: DYNAMIC_ROLES,
    });

    if (hasCommonContexts) {
      return {
        allowed: true,
        reason: 'Nutzer ist PSB für die Person und darf Microsoft sehen',
      };
    }

    return {
      allowed: false,
      reason: 'Nutzer darf Microsoft sehen.',
    };
  },

  /**
   * Only the person itself can view their own SEPA mandates and all persons which have accounting administrative permissions
   */
  [UserDetailsActions.VIEW_SEPA_MANDATES]: async request => {
    /**
     * Check if it is the user himself, He can see his own data
     */
    if (isSelf(request.payload.currentUser, request.payload.viewedUser)) {
      return { allowed: true, reason: 'Nutzer kann sich selbst sehen.' };
    }

    /**
     * Check if the currentUser can see the employee
     */
    const allowedRolesOnUsers = [Role.BUCHHALTUNG, Role.SPENDEN_ADMIN];

    const hasCommonContexts = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnUsers,
      rolesViewedUser: DYNAMIC_ROLES,
    });
    if (hasCommonContexts) {
      return {
        allowed: true,
        reason: 'Nutzer darf die Mitarbeiterdaten sehen. Da er eine sehende Rolle auf der gleichen Schule hat.',
      };
    }

    return {
      allowed: false,
      reason: 'Nutzer ist nicht an der gleichen Schule.',
    };
  },

  [UserDetailsActions.VIEW_STUDENT_TAB]: async request => {
    /**
     * Check if it is the user himself, He can see his own data
     */
    if (isSelf(request.payload.currentUser, request.payload.viewedUser)) {
      return { allowed: true, reason: 'Nutzer kann sich selbst sehen.' };
    }

    /**
     * Check if the user is a parent of the viewed user
     */
    if (isParentOf(request.payload.currentUser, request.payload.viewedUser, ['sorgeberechtigt'])) {
      return { allowed: true, reason: 'Eltern können die Schülerdaten ihrer Kinder sehen.' };
    }

    /**
     * Check if the currentUser can see the employee
     */
    const allowedRolesOnUsers = [Role.SEKRETAER, Role.SEKRETAER, Role.SUS_BEWERBER_BEARBEITUNG];

    const hasCommonContexts = await usersHaveSameSchoolOnRoles({
      currentUser: request.payload.currentUser,
      viewedUser: request.payload.viewedUser,
      rolesCurrentUser: allowedRolesOnUsers,
      rolesViewedUser: STUDENT_ROLES,
    });
    if (hasCommonContexts) {
      return {
        allowed: true,
        reason: 'Nutzer darf die Schülerdaten sehen. Da er eine sehende Rolle auf der gleichen Schule hat.',
      };
    }

    return {
      allowed: false,
      reason: 'Nutzer ist nicht an der gleichen Schule.',
    };
  },

  [UserDetailsActions.REMOVE_CHILDREN_FROM_SBS_TEMP]: async () => {
    return {
      allowed: false,
      reason: 'Nutzer ist nicht an der gleichen Schule.',
    };
  },
};

export const UserDetailsPolicy: IPolicy<UserOnUserPayload> = {
  name: 'UserDetailsPolicy',
  acceptingPayloadTypes: [MARTINUS_ABAC_PAYLOAD],
  async evaluate(request) {
    /**
     * Check logged in user
     */
    request.payload = ensureLoggedInPayload(request.payload);

    /**
     * Execute the correct function for the action
     */
    const result = await UserDetailsPolicyFunctions[request.payload.action]?.(request);

    if (isNullish(result)) return false;
    return result;
  },
};
